import { Injectable } from '@angular/core';
import { AuthService } from '@kimaya/ngx-auth-service';
import { HttpService, HttpServiceConstants } from '@kimaya/ngx-http-service';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import { FROALA_KEY } from '../license-keys';
// tslint:disable-next-line:import-spacing
import FroalaEditor from 'froala-editor';

interface FroalaEditorSetting {
  key: string;
  placeholderText?: string;
  imageUploadMethod?: string;
  imageUploadURL?: string;
  imageManagerLoadUrl?: any[];
  imageManagerLoadMethod?: string;
  imageManagerPageSize?: number;
  height: number;
  toolbarButtons?: string[];
  toolbarButtonsMD?: string[];
  toolbarButtonsSM?: string[];
  toolbarButtonsXS?: string[];
  convertMailAddresses?: boolean;
  plainPaste?: boolean;
  pluginsEnabled: string[];
  inlineMode: boolean;
  tabSpaces: boolean;
  htmlExecuteScripts: boolean;
  requestHeaders?: any;
  linkAutoPrefix: string;
  htmlRemoveTags: string[];
  attribution: boolean;
  toolbarSticky: boolean;
}

const froalaKey = FROALA_KEY;

@Injectable()
export class FroalaEditorService {
  private commonToolbarActions = [
    'bold', 'italic', 'underline', 'align',
    'formatOL', 'formatUL', 'insertTable', 'undo', 'redo',
  ];
  private froalaOptions: FroalaEditorSetting = {
    key: froalaKey,
    placeholderText: 'Type Something',
    imageUploadURL: '',
    imageUploadMethod: 'POST',
    imageManagerPageSize: 1,
    height: 300,
    toolbarButtons: [...this.commonToolbarActions],
    toolbarButtonsMD: [...this.commonToolbarActions],
    toolbarButtonsSM: [...this.commonToolbarActions],
    toolbarButtonsXS: [...this.commonToolbarActions],
    convertMailAddresses: false,
    plainPaste: true,
    pluginsEnabled: ['fontFamily', 'fontSize', 'align', 'lists', 'link', 'table', 'colors', 'image'],
    inlineMode: false,
    tabSpaces: false,
    htmlExecuteScripts: false,
    linkAutoPrefix: '',
    htmlRemoveTags: ['img'],
    attribution: false,
    toolbarSticky: false,
  };
  private editor: any;

  constructor(private readonly webService: HttpService,
              private readonly translateService: TranslateService,
              private readonly authService: AuthService) {
  }

  private getRequestOptions() {
    const httpHeader = {
      Authorization: `Bearer ${this.authService.getAccessToken()}`,
    };

    /*Set Extra Headers from HttpServiceConstants*/
    if (Object.keys(HttpServiceConstants.headers || {}).length > 0) {
      for (const headerDataKey in HttpServiceConstants.headers) {
        if (HttpServiceConstants.headers.hasOwnProperty(headerDataKey)) {
          httpHeader[headerDataKey] = HttpServiceConstants.headers[headerDataKey];
        }
      }
    }
    return httpHeader;
  }

  public getFroalaOptions(pText?: string, imgUrl?: string): FroalaEditorSetting {
    this.froalaOptions.imageUploadURL = imgUrl;
    this.froalaOptions.placeholderText = pText;
    this.froalaOptions.requestHeaders = this.getRequestOptions();
    this.froalaOptions['events'] = {
      initialized: (e) => {
        this.editor = e._editor;
        this.setSanitizeURLHelper(this.editor);
      },
    };
    return _.cloneDeep(this.froalaOptions);
  }

  addDropDown(data: object, title: string, icon: string): void {
    FroalaEditor.DefineIcon('dropDown', {NAME: 'dropDown'});
    FroalaEditor.RegisterCommand('dropDown', {
      title,
      icon,
      type: 'dropdown',
      focus: true,
      undo: false,
      refreshAfterCallback: true,
      options: data,
      callback(cmd: any, val: any) {
        this.html.insert(val);
      },
      refresh: ($btn: any) => {
      },
      refreshOnShow: ($btn: any, $dropdown: any) => {
      },
    });
  }

  public insertFrolaText(text: string, divId: string): void {
    setTimeout(() => {
      const div = document.getElementById(divId);
      div.insertAdjacentHTML('beforeend', text);
    }, 0);
  }

  setSanitizeURLHelper(editor: any) {
    if (editor?.helpers) {
      editor.helpers.sanitizeURL = (url) => {
        return this.sanitizeText(url);
      };
    }
  }

  sanitizeText(text): string {
    return decodeURIComponent(text)
      .replace(/%23/g, '#')
      .replace(/%24/g, '$')
      .replace(/%25/g, '%')
      .replace(/%26/g, '&')
      .replace(/&amp;/g, '&')
      .replace(/mailto%3A/gi, 'mailto:')
      .replace(/file%3A/gi, 'file:')
      .replace(/sms%3A/gi, 'sms:')
      .replace(/tel%3A/gi, 'tel:')
      .replace(/notes%3A/gi, 'notes:')
      .replace(/data%3Aimage/gi, 'data:image')
      .replace(/blob%3A/gi, 'blob:')
      .replace(/webkit-fake-url%3A/gi, 'webkit-fake-url:')
      .replace(/%2B/g, '+')
      .replace(/%2C/g, ',')
      .replace(/%2F/g, '/')
      .replace(/%3B/g, ';')
      .replace(/%3D/g, '=')
      .replace(/%3F/g, '?')
      .replace(/%40/g, '@')
      .replace(/%5B/g, '[')
      .replace(/%5D/g, ']')
      .replace(/%7B/g, '{')
      .replace(/%7D/g, '}');
  }
}
